import _ from 'lodash';

import utils   from 'app/helpers/utils';
import Metrics from 'app/metrics';

const { buildQueryString: qs } = utils;

const pathifyName = (name) => {
  const normalizedName = _.deburr(name || '')
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/_/g, '-')
    .toLowerCase();
  if (name.trim() && !normalizedName) return '-';
  return normalizedName;
};

const buildNonprofitsQueryString = (opts={}) => {
  const {category, nteePath, search, postalCode, sort, distance, badges, companyId, country, page, inFavorites, countryCode, cityId, campaignId} = opts;
  const fixedBadges = Array.isArray(badges) ? badges.join(',') : badges;
  const params = {search, postalCode, sort, distance, badges: fixedBadges, companyId, country, page, cityId, campaignId};
  if (countryCode != '*') {
    params.countryCode = countryCode;
  }
  if (inFavorites === true) {
    params.inFavorites = true;
  }
  if (!sort && !distance) {
    params.postalCode = null;
  }
  return qs(params);
};

const paths = {

  home: '/',
  homeDemo: '/#demo',
  login: (postLoginUrl, query={}) => {
    return `/login${qs({...query, postLoginUrl})}`;
  },
  onboarding: (postLoginUrl, query={}) => {
    return `/onboarding${qs({...query, postLoginUrl})}`;
  },
  ssoLogin: '/sso-login',
  signup: (postLoginUrl, query={}) => {
    return `/signup${qs({...query, postLoginUrl})}`;
  },
  mfa: () => '/2fa',
  signupEmp: () => `/signup-employee`,
  signupInd: () => `/signup-individual`,
  forgotPassword: (query={}) => `/forgot-password${qs(query)}`,
  setNewPassword: (evId) => `/new-password/${evId}`,
  newPassword: () => `/account/new-password`,
  resendEmpInvite: () => '/resend-employee-invite',
  approvalRequired: () => '/approval-required',
  styleGuide: '/style-guide',
  verifyEmailPage: '/verify-email',
  verifyEmail: (id) => `/verify-email/${id}`,
  nonprofits: (query={}) => {
    let path = '/nonprofits';
    if (query.nteePath) {
      path += `/ntee/${query.nteePath}`;
    }
    path += buildNonprofitsQueryString(query);
    return path;
  },
  nonprofit: ({ein, name, countryCode, id}, query={}) => {
    const fixedName = pathifyName(name);
    return ein
      ? `/nonprofits/${ein}/${fixedName}${qs(query)}`
      : `/nonprofits/${countryCode}/${id}/${fixedName}${qs(query)}`;
  },
  nonprofitProfileEdit: (id) => `/nonprofit-profiles/${id}/edit`,
  nonprofitProfilePreview: (id) => `/nonprofit-profiles/${id}/preview`,
  nonprofitDashboard: (id) => `/nonprofits/${id}/dashboard`,
  nteeCodes: (query={}) => `/nonprofits/ntee${qs(query)}`,
  nteeCode: ({code, name}, query={}) => {
    const fixedName = pathifyName(name);
    return `/nonprofits/ntee/${code}-${fixedName}${buildNonprofitsQueryString(query)}`;
  },
  creditCards: '/credit-cards',
  wallet: ({nonprofitId} = {}) => `/wallet${qs({nonprofitId})}`,
  offlineDonation: (id, query={}) => `/offline-donations/${id}${qs(query)}`,
  offlineDonationsNew: (query={}) => `/offline-donations/new${qs(query)}`,
  claim: (id) => (id ? `/claim?nonprofitId=${id}` : '/claim'),
  giftsSend: '/gift',
  giftsRedeem: (code) => `/redeem${qs({code})}`,
  user: (id) => `/users/${id}`,
  account: ({showEmailChangeSuccess = false} = {}) => {
    const query = {
      emailChangeSuccess: showEmailChangeSuccess || null,
    };
    return `/account${qs(query)}`;
  },
  collection: (collection) => `/collections/${collection.path}`,
  collections: () => '/collections',
  employeeInvite: (token) => `/employees/invite/${token}`,
  companyDashboard: (id, query={}) => `/company-dashboards/${id}${qs(query)}`,
  campaign: (id, query={}) => `/campaigns/${id}${qs(query)}`,
  groups: (id) => `/groups`,
  group: (id, query={}) => `/groups/${id}${qs(query)}`,
  brackets: (id) => `/brackets`,
  bracket: (id) => `/brackets/${id}`,
  bracketGame: (id) => `/bracket-games/${id}`,
  volunteer: (query={}) => `/volunteer${qs(query)}`,
  volunteerMe: (query={}) => `/volunteer/me${qs(query)}`,
  volEvent: (id, query={}) => `/volunteer/${id}${qs(query)}`,
  volEventShiftIcs: (volEventId, volEventShiftId) => `/api/vol-events/${volEventId}/vol-event-shifts/${volEventShiftId}/ics`,
  page: (urlSlug) => `/pages/${urlSlug}`,
  slackConnectNonce: (id) => `/slack/connect-nonce/${id}`,
  slackInstall: () => '/slack/install',
  slackDirectInstall: () => '/slack/direct-install',
  donatable: (donatable) => donatable?.ein ? paths.nonprofit(donatable) : `/donatables/${donatable?.id}`,
  happenings: (query={}) => `/events${qs(query)}`,
  globalSearch: (query={}) => `/global-organization-search${qs(query)}`,
  everyCent: (query={}) => `/every-cent${qs(query)}`,
  socialPost: (postId, query={}) => `/social/posts/${postId}${qs(query)}`,
  form: (formId, query={}) => `/forms/${formId}${qs(query)}`,
  formSubmission: (submissionId, query={}) => `/forms/submissions/${submissionId}${qs(query)}`,
  formSubmissionEdit: (submissionId, query={}) => `/forms/submissions/${submissionId}/edit${qs(query)}`,
  grantPrograms: (companySlug, query={}) => `/grant-programs/company/${companySlug}${qs(query)}`,
  grantProgram: (grantProgramId, query={}) => `/grant-programs/${grantProgramId}${qs(query)}`,
  grantProgramPrint: (grantProgramId, query={}) => `/grant-programs/${grantProgramId}/print${qs(query)}`,
  grantProgramApply: (grantProgramId, query={}) => `/grant-programs/${grantProgramId}/apply${qs(query)}`,
  grantApp: (grantAppId, query={}) => `/grant-apps/${grantAppId}${qs(query)}`,
  grantApps: (query={}) => `/grant-apps${qs(query)}`,

  /*
   *  Simple Pages
   */

  privacyPolicy: '/privacy-policy',
  tos: '/terms',

  /*
   *  Company Admin
   */

  cadminEmployees: (companySlug, query={}) => `/cadmin/${companySlug}/employees${qs(query)}`,
  cadminEmployeesUpload: (companySlug, query={}) => `/cadmin/${companySlug}/employees/upload${qs(query)}`,
  cadminEmployee: (companySlug, employeeId, query={}) => `/cadmin/${companySlug}/employees/${employeeId}${qs(query)}`,
  cadminMatch: (companySlug, edId, query={}) => `/cadmin/${companySlug}/matches/${edId}${qs(query)}`,
  cadminMatches: (companySlug, query={}) => `/cadmin/${companySlug}/matches${qs(query)}`,
  cadminMatchSettings: (companySlug) => `/cadmin/${companySlug}/match-settings`,
  cadminNonprofits: (companySlug, query={}) => `/cadmin/${companySlug}/nonprofits${qs(query)}`,
  cadminNteeCodes: (companySlug, query={}) => `/cadmin/${companySlug}/ntee-codes${qs(query)}`,
  cadminMatchInquiries: (companySlug, query={}) => `/cadmin/${companySlug}/match-inquiries${qs(query)}`,
  cadminOfflineDonations: (companySlug, query={}) => `/cadmin/${companySlug}/offline-donations${qs(query)}`,
  cadminWallet: (companySlug, query={}) => `/cadmin/${companySlug}/wallet${qs(query)}`,
  cadminTransactions: (companySlug, query={}) => `/cadmin/${companySlug}/transactions${qs(query)}`,
  cadminTransactions2: (companySlug, query={}) => `/cadmin/${companySlug}/transactions2${qs(query)}`,
  cadminPurchase: (companySlug, purchaseId, query={}) => `/cadmin/${companySlug}/purchases/${purchaseId}${qs(query)}`,
  cadminDashboardEdit: (companySlug, dashboardId, query={}) => `/cadmin/${companySlug}/dashboards/${dashboardId}/edit${qs(query)}`,
  cadminSlackSettings: (companySlug, query={}) => `/cadmin/${companySlug}/slack-settings${qs(query)}`,
  cadminIntsCalendar: (companySlug, query={}) => `/cadmin/${companySlug}/integrations/calendar${qs(query)}`,
  cadminSettingsApprovals: (companySlug, query={}) => `/cadmin/${companySlug}/settings/approvals${qs(query)}`,
  cadminDashboardView: (companySlug, dashboardId, query={}) => `/cadmin/${companySlug}/dashboards/${dashboardId}${qs(query)}`,
  cadminDashboardViewWithoutId: (companySlug) => `/cadmin/${companySlug}/dashboard`,
  cadminVolEvents: (companySlug, query={}) => `/cadmin/${companySlug}/vol-events${qs(query)}`,
  cadminVolEventsView: (companySlug, volEventId, query={}) => `/cadmin/${companySlug}/vol-events/${volEventId}${qs(query)}`,
  cadminVolAnalytics: (companySlug, query={}) => `/cadmin/${companySlug}/vol-analytics${qs(query)}`,
  cadminVolTracking: (companySlug, query={}) => `/cadmin/${companySlug}/vol-tracking${qs(query)}`,
  cadminVolDfd: (companySlug, query={}) => `/cadmin/${companySlug}/dollars-for-doers${qs(query)}`,
  cadminNewVolEvent: (companySlug, query={}) => `/cadmin/${companySlug}/vol-events/new${qs(query)}`,
  cadminEditVolEvent: (companySlug, volEventId, query={}) => `/cadmin/${companySlug}/vol-events/${volEventId}/edit${qs(query)}`,
  cadminIcsVolEvent: (companySlug, volEventId) => `/api/cadmin/${companySlug}/vol-events/${volEventId}/ics`,
  cadminCampaigns: (companySlug, query={}) => `/cadmin/${companySlug}/campaigns${qs(query)}`,
  cadminCampaignTemplates: (companySlug, query={}) => `/cadmin/${companySlug}/campaigns/templates${qs(query)}`,
  cadminNewCampaign: (companySlug, query={}) => `/cadmin/${companySlug}/campaigns/new${qs(query)}`,
  cadminEditCampaign: (companySlug, campaignId, query={}) => `/cadmin/${companySlug}/campaigns/${campaignId}/edit${qs(query)}`,
  cadminCampaignsView: (companySlug, campaignId, query={}) => `/cadmin/${companySlug}/campaigns/${campaignId}${qs(query)}`,
  cadminGifts: (companySlug, query={}) => `/cadmin/${companySlug}/gifts${qs(query)}`,
  cadminGiftsSend: (companySlug, query={}) => `/cadmin/${companySlug}/gifts/new${qs(query)}`,
  cadminGrants: (companySlug, query={}) => `/cadmin/${companySlug}/grants${qs(query)}`,
  cadminGrantsCreate: (companySlug, query={}) => `/cadmin/${companySlug}/grants/new${qs(query)}`,
  cadminGroups: (companySlug, query={}) => `/cadmin/${companySlug}/groups${qs(query)}`,
  cadminGroupEmployees: (companySlug, query={}) => `/cadmin/${companySlug}/groups/members${qs(query)}`,
  cadminGroupsSettings: (companySlug) => `/cadmin/${companySlug}/groups/settings`,
  cadminGroupsNew: (companySlug) => `/cadmin/${companySlug}/groups/new`,
  cadminGroupsEdit: (companySlug, groupId) => `/cadmin/${companySlug}/groups/${groupId}/edit`,
  cadminGroupsView: (companySlug, groupId, query={}) => `/cadmin/${companySlug}/groups/${groupId}${qs(query)}`,
  cadminPayroll: (companySlug, query={}) => `/cadmin/${companySlug}/payroll${qs(query)}`,
  cadminSso: (companySlug, query={}) => `/cadmin/${companySlug}/sso${qs(query)}`,
  cadminBrackets: (companySlug, query={}) => `/cadmin/${companySlug}/brackets${qs(query)}`,
  cadminBracketsNew: (companySlug) => `/cadmin/${companySlug}/brackets/new`,
  cadminBracketsEdit: (companySlug, bracketId) => `/cadmin/${companySlug}/brackets/${bracketId}/edit`,
  cadminBracketsDashboard: (companySlug, bracketId) => `/cadmin/${companySlug}/brackets/${bracketId}/dashboard`,
  cadminSubscription: (companySlug) => `/cadmin/${companySlug}/subscription`,
  cadminBudgets: (companySlug) => `/cadmin/${companySlug}/budgets`,
  cadminDriveDonations: (companySlug, query={}) => `/cadmin/${companySlug}/drive-donations${qs(query)}`,
  cadminCards: (companySlug, query={}) => `/cadmin/${companySlug}/cards${qs(query)}`,
  cadminAnalytics: (companySlug, query={}, presetKey='impact') => {
    const preset = Metrics.presets[presetKey];
    const combinedQuery = {...preset.defaultQuery, ...query};
    return `/cadmin/${companySlug}/analytics/${preset.key}${qs(combinedQuery)}`;
  },
  cadminCattrs: (companySlug, query={}) => `/cadmin/${companySlug}/custom-attributes${qs(query)}`,
  cadminBillingNotifications: (companySlug) => `/cadmin/${companySlug}/billing-notifications`,
  cadminChargeError: (companySlug, cceId) => `/cadmin/${companySlug}/charge-errors/${cceId}`,
  cadminUpcomingCharges: (companySlug, query={}) => `/cadmin/${companySlug}/upcoming-charges${qs(query)}`,
  cadminForms: (companySlug, query={}) => `/cadmin/${companySlug}/forms${qs(query)}`,
  cadminFormsNew: (companySlug, query={}) => `/cadmin/${companySlug}/forms/new${qs(query)}`,
  cadminFormsShow: (companySlug, formId, query={}) => `/cadmin/${companySlug}/forms/${formId}${qs(query)}`,
  cadminFormsEdit: (companySlug, formId, query={}) => `/cadmin/${companySlug}/forms/${formId}/edit${qs(query)}`,
  cadminGrantPrograms: (companySlug, query={}) => `/cadmin/${companySlug}/grant-programs${qs(query)}`,
  cadminGrantProgram: (companySlug, grantProgramId, query={}) => `/cadmin/${companySlug}/grant-programs/${grantProgramId}${qs(query)}`,
  cadminGrantProgramEditApp: (companySlug, grantProgramId, query={}) => `/cadmin/${companySlug}/grant-programs/${grantProgramId}/edit-application-flow${qs(query)}`,
  cadminGrantProgramEditLand: (companySlug, grantProgramId, query={}) => `/cadmin/${companySlug}/grant-programs/${grantProgramId}/edit-landing-page${qs(query)}`,
  cadminGrantApp: (companySlug, grantAppId, tab='timeline', query={}) => `/cadmin/${companySlug}/grant-apps/${grantAppId}/${tab}${qs(query)}`,
  cadminGrantSysSettings: (companySlug, tab='forms', query={}) => `/cadmin/${companySlug}/grant-settings/${tab}${qs(query)}`,
  cadminGrantWorkflowsNew: (companySlug, query={}) => `/cadmin/${companySlug}/grant-workflows/new${qs(query)}`,
  cadminGrantWorkflowsEdit: (companySlug, grantWorkflowId, query={}) => `/cadmin/${companySlug}/grant-workflows/${grantWorkflowId}/edit${qs(query)}`,
  cadminGrantMyTasks: (companySlug, tab='assigned', query={}) => `/cadmin/${companySlug}/my-grants-tasks/${tab}${qs(query)}`,
  cadminGrantBudgets: (companySlug, query={}) => `/cadmin/${companySlug}/grant-budgets${qs(query)}`,
  cadminGrantAssignment: (companySlug, grantAssignmentId, query={}) => `/cadmin/${companySlug}/grant-assignments/${grantAssignmentId}${qs(query)}`,

  /*
   *  Company Admin CSVs
   */

  cadminMatchesCsv:              (companySlug, query={}) => `/api/company-admin/${companySlug}/employee-donations${qs({...query, csv: true})}`,
  cadminTransactionsCsv2:        (companySlug, query={}) => `/api/company-admin/${companySlug}/balance-transactions${qs({...query, csv: true})}`,
  cadminVolEventsCsv:            (companySlug, query={}) => `/api/company-admin/${companySlug}/vol-events${qs({...query, csv: true})}`,
  cadminVolAnalyticsCsv:         (companySlug, query={}) => `/api/company-admin/${companySlug}/vol-time-entries/grouped${qs({...query, csv: true})}`,
  cadminPurchaseCsv: (companySlug, purchaseId, query={}) => `/api/company-admin/${companySlug}/company-purchases/${purchaseId}${qs({...query, csv: true})}`,
  cadminGroupEmployeesCsv:       (companySlug, query={}) => `/api/company-admin/${companySlug}/groups/group-employees${qs({...query, csv: true})}`,
  cadminEmployeesCsv:            (companySlug, query={}) => `/api/company-admin/${companySlug}/employees${qs({...query, csv: true})}`,
  cadminEmployeesTemplateCsv:    (companySlug, query={}) => `/api/company-admin/${companySlug}/employees/template${qs({...query})}`,
  cadminVolTimeEntriesCsv:       (companySlug, query={}) => `/api/company-admin/${companySlug}/vol-time-entries${qs({...query, csv: true})}`,
  cadminGiftsCsv:                (companySlug, query={}) => `/api/company-admin/${companySlug}/gifts${qs({...query, csv: true})}`,
  cadminGrantsCsv:               (companySlug, query={}) => `/api/company-admin/${companySlug}/grants${qs({...query, csv: true})}`,
  cadminStatsCsv:                (companySlug, query={}) => `/api/company-admin/${companySlug}/stats${qs({...query, csv: true})}`,
  cadminDriveDonationsCsv:       (companySlug, query={}) => `/api/company-admin/${companySlug}/drive-donations${qs({...query, csv: true})}`,
  cadminAnalyticsCsv:            (companySlug, query={}) => `/api/company-admin/${companySlug}/analytics${qs({...query, csv: true})}`,
  cadminOfflineDonationsCsv:     (companySlug, query={}) => `/api/company-admin/${companySlug}/offline-donations${qs({...query, csv: true})}`,

  /*
   *  Company Admin Redirects
   */

  cadminRedirectConnectCalendar: (companySlug, query={}) => `/api/company-admin/${companySlug}/integrations/nylas/start${qs(query)}`,

  /*
   *  SAML & SCIM
   */

  samlAcs:      (companySlug) => `/saml/${companySlug}/acs`,
  samlMetadata: (companySlug) => `/saml/${companySlug}/metadata`,
  samlLogin:    (companySlug) => `/saml/${companySlug}/login`,
  scimBase:     (companySlug) => `/scim/${companySlug}`,

  /*
   *  Backstage
   */

  bs: () => '/backstage',
  bsPendingClaims: () => '/backstage/pending-claims',
  bsFailedCharges: () => '/backstage/failed-charges',
  bsNonprofitProfiles: ({page=1} = {}) => `/backstage/nonprofit-profiles?page=${page}`,
  bsUsers: ({page=1, sort='join-date', search} = {}) => `/backstage/users${qs({page, sort, search})}`,
  bsUser: (id) => `/backstage/users/${id}`,
  bsCollections: () => '/backstage/collections',
  bsNonprofits: (query={}) => `/backstage/nonprofits${qs(query)}`,
  bsNonprofit: (id, query={}) => `/backstage/nonprofits/${id}${qs(query)}`,
  bsCompanies: (query={}) => `/backstage/companies${qs(query)}`,
  bsCompany: (id) => `/backstage/companies/${id}`,
  bsPages: (query={}) => `/backstage/pages${qs(query)}`,
  bsCards: (query={}) => `/backstage/cards${qs(query)}`,
  bsDashboardOverview: (query={}) => `/backstage/dashboard/overview${qs(query)}`,
  bsFeatureFlags: () => `/backstage/feature-flags`,
  bsPricing: (query={}) => `/backstage/pricing${qs(query)}`,
  bsConfig: () => `/backstage/config`,
  bsBalance: (id, query={}) => `/backstage/balances/${id}${qs(query)}`,
  bsBalances: (query={}) => `/backstage/balances${qs(query)}`,
  bsBalancesTransfer: (query={}) => `/backstage/balances/transfer`,
  bsNteeCodes: (query={}) => `/backstage/ntee-codes`,
  bsNonprofitRequests: (query={}) => `/backstage/nonprofit-requests${qs(query)}`,
  bsDisbursements: (query={}) => `/backstage/disbursements${qs(query)}`,
  bsDisbursementsCsv: (query={}) => `/api/backstage/disbursements${qs({...query, csv: true})}`,
  bsDisbursement: (id) => `/backstage/disbursements/${id}`,
  bsPvss: () => `/backstage/pvss`,


  /*
   *  Integrations
   */

  stripeRedirectSetupIntent: () => `/stripe/redirect/setup-intent`,

};

export default paths;
